import React, {useMemo, useState} from 'react';
import {Checkbox, FlexColumn, Heading, Link, Modal, SubHeading, Text} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import Util from '../../shared/util/util';
import {createToast} from '../../shared/components/toasts/toast';
import UpdateProfileMutation from '../../../mutations/UpdateProfileMutation';
import {PERMISSION_TYPE} from '../../../Permissions';
import {hasModule} from '../../shared/util/ModuleUtil';
import {MODULE_TYPES} from '../../../constants';
import {CLIENT_PERMISSIONS_WITH_GUEST_USER} from './ClientPermissionGroups';

type PermissionGroup = {
	name: string;
	description?: string;
	permissions: string[];
};

type CompanyProfile = {
	id: string;
	name: string;
	permissions: string[];
	canBeEdited: boolean;
	personsCount: number;
	disabled: boolean;
};

interface PermissionProfileProps {
	closeModal: () => void;
	profile: CompanyProfile;
}

const HELP_CENTER_URL = 'https://support.forecast.app/hc/en-us/articles/5446751642385-Adding-and-managing-Guest-Users';

const ClientPermissionsModal = ({closeModal, profile}: PermissionProfileProps): JSX.Element => {
	const {formatMessage} = useIntl();

	const [permissions, setPermissions] = useState<Set<string>>(new Set([...profile.permissions]));
	const hasSisenseModule = useMemo(() => hasModule(MODULE_TYPES.SISENSE), []);
	const permissionGroups = useMemo<PermissionGroup[]>(() => {
		return CLIENT_PERMISSIONS_WITH_GUEST_USER.filter(group => {
			if (group.permissions.includes(PERMISSION_TYPE.SISENSE_READ)) {
				// Only show sisense groups for people with sisense module
				return hasSisenseModule;
			}

			return true;
		});
	}, []);

	const helpCenterLink = (
		<Link href={HELP_CENTER_URL}>
			<FormattedMessage id={'common.help_center_article'} />
		</Link>
	);

	const onSave = () => {
		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: formatMessage({id: 'settings.profile_updated'}),
			});

			closeModal();
		};

		const input = {
			id: profile.id,
			name: profile.name,
			profilePermissions: Array.from(permissions),
		};

		Util.CommitMutation(UpdateProfileMutation, input, onSuccess);
	};

	const isChecked = (permissionGroup: PermissionGroup): boolean => {
		let isChecked = true;

		for (const permission of permissionGroup.permissions) {
			if (!permissions.has(permission)) {
				isChecked = false;
				break;
			}
		}

		return isChecked;
	};

	const onPermissionGroupSelect = (permissionGroup: PermissionGroup): void => {
		const isSelected: boolean = isChecked(permissionGroup);

		for (const permission of permissionGroup.permissions) {
			if (isSelected) {
				permissions.delete(permission);
			} else {
				permissions.add(permission);
			}
		}

		setPermissions(new Set(permissions));
	};

	return (
		<Modal onCloseModal={closeModal}>
			<Modal.Title>
				<Heading size={3}>
					<FormattedMessage id={'settings.edit_client_permissions'} />
				</Heading>
			</Modal.Title>

			<Modal.Content>
				<Text>
					<FormattedMessage id={'settings.edit_client_permissions_description'} />
				</Text>

				<Text>
					<FormattedMessage
						id={'settings.edit_client_permissions_help_center_article'}
						values={{link: helpCenterLink}}
					/>
				</Text>

				<FlexColumn>
					<SubHeading>
						<FormattedMessage id="common.permissions" />
					</SubHeading>

					<Text color={'medium'}>
						<FormattedMessage id={'settings.modify_client_permission_options'} />
					</Text>
				</FlexColumn>

				<FlexColumn gap={'xl'} alignItems={'start'}>
					{permissionGroups.map(permissionGroup => (
						<Checkbox
							checked={isChecked(permissionGroup)}
							onChange={() => onPermissionGroupSelect(permissionGroup)}
							labelText={permissionGroup.name}
							description={permissionGroup.description}
						/>
					))}
				</FlexColumn>
			</Modal.Content>

			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={onSave}>
					<FormattedMessage id={'common.save'} />
				</Modal.PrimaryFooterButton>

				<Modal.SecondaryFooterButton onPress={closeModal}>
					<FormattedMessage id={'common.close'} />
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default ClientPermissionsModal;
